<template>
  <span :class="wrapperClass" ref="root" aria-hidden="false">
    <InlineSvg :src="src" ref="inlineComp" v-bind="inlineProps">
      <template #fallback>
        <slot name="fallback"></slot>
      </template>
    </InlineSvg>
  </span>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import InlineSvg from 'vue-inline-svg'

const props = defineProps({
  src: { type: String, required: true },
  size: { type: [String, Number], default: '' },
  width: { type: [String, Number], default: '' },
  height: { type: [String, Number], default: '' },
  svgClass: { type: [String, Array, Object], default: '' },
  wrapperClass: { type: [String, Array, Object], default: '' },
  fill: { type: String, default: 'currentColor' },      // по умолчанию inherit
  stroke: { type: String, default: 'currentColor' },    // по умолчанию inherit
  title: { type: String, default: '' },
  viewBox: { type: String, default: '' },
  preserveAspectRatio: { type: String, default: '' },
  removeSizeAttrs: { type: Boolean, default: false },
  extraAttrs: { type: Object, default: () => ({}) },
  inlineProps: { type: Object, default: () => ({}) }
})

const root = ref(null)
const inlineComp = ref(null)
let observer = null

// найти <svg> внутри root
function findSvgEl() {
  if (!root.value) return null
  return root.value.querySelector && root.value.querySelector('svg')
}

function applyAttrsToSvg(svgEl) {
  if (!svgEl) return

  // CSS-класс
  if (props.svgClass) {
    const existing = svgEl.getAttribute('class') || ''
    svgEl.setAttribute('class', [existing, props.svgClass].filter(Boolean).join(' ').trim())
  }

  // размеры
  if (props.removeSizeAttrs) {
    svgEl.removeAttribute('width')
    svgEl.removeAttribute('height')
    svgEl.style.width = props.size ? `${props.size}rem` : '1rem'
    svgEl.style.height = props.size ? `${props.size}rem` : '1rem'
  } else {
    if (props.width) svgEl.setAttribute('width', String(props.width))
    if (props.height) svgEl.setAttribute('height', String(props.height))
    if (!props.width && !props.height && props.size) {
      svgEl.setAttribute('width', String(props.size))
      svgEl.setAttribute('height', String(props.size))
    }
  }

  // fill / stroke по умолчанию inherit
  svgEl.setAttribute('fill', props.fill || 'currentColor')
  svgEl.setAttribute('stroke', props.stroke || 'currentColor')

  // viewBox / preserveAspectRatio
  if (props.viewBox) svgEl.setAttribute('viewBox', props.viewBox)
  if (props.preserveAspectRatio) svgEl.setAttribute('preserveAspectRatio', props.preserveAspectRatio)

  // title
  if (props.title) {
    const existingTitle = svgEl.querySelector('title')
    if (existingTitle) existingTitle.textContent = props.title
    else {
      const t = document.createElementNS('http://www.w3.org/2000/svg', 'title')
      t.textContent = props.title
      svgEl.insertBefore(t, svgEl.firstChild)
    }
  }

  // extra attrs
  for (const [k, v] of Object.entries(props.extraAttrs || {})) {
    if (v === null || v === undefined) svgEl.removeAttribute(k)
    else svgEl.setAttribute(k, String(v))
  }
}

// MutationObserver для применения атрибутов
function startObserver() {
  if (!root.value) return
  const existing = findSvgEl()
  if (existing) applyAttrsToSvg(existing)

  observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'childList' && m.addedNodes.length) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1 && node.nodeName.toLowerCase() === 'svg') {
            applyAttrsToSvg(node)
            return
          } else if (node.nodeType === 1) {
            const nested = node.querySelector && node.querySelector('svg')
            if (nested) {
              applyAttrsToSvg(nested)
              return
            }
          }
        }
      }
    }
  })
  observer.observe(root.value, { childList: true, subtree: true })
}

onMounted(async () => {
  await nextTick()
  startObserver()
})

watch(() => props.src, async () => {
  await nextTick()
  setTimeout(() => {
    const svg = findSvgEl()
    if (svg) applyAttrsToSvg(svg)
  }, 20)
})

onBeforeUnmount(() => {
  if (observer) observer.disconnect()
})
</script>

<style scoped>
span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}

/* Размер и цвет наследуются от родителя по умолчанию */
span svg {
  display: inline-block;
  color: currentColor;
  vertical-align: middle;
  fill: currentColor;
}
</style>
